const configuredApiUrl = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
export const API_BASE_URL = configuredApiUrl.endsWith('/api') ? configuredApiUrl : `${configuredApiUrl}/api`;
export const SERVER_URL = API_BASE_URL.replace(/\/api$/, '');

export const resolveProductImageUrl = (imageUrl?: string): string => {
  if (!imageUrl) return '/images/product-placeholder.jpg';
  if (/^https?:\/\//i.test(imageUrl)) return imageUrl;
  if (imageUrl.startsWith('/images/')) return imageUrl;
  const filename = imageUrl.replace(/^\/(?:api\/)?uploads\//, '').replace(/^\//, '');
  return `${API_BASE_URL}/uploads/${filename}`;
};

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  fullName: string;
  email: string;
  password: string;
  phoneNumber: string;
  role: string;
  farmLocation?: string;
  farmSize?: string | number;
  farmName?: string;
  cropCategories?: string[];
  businessName?: string;
}

interface UpdateProfileData {
  fullName: string;
  phoneNumber: string;
  farmLocation?: string;
  farmSize?: string;
}

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

class ApiService {
  private getAuthHeader(): Record<string, string> {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async login(credentials: LoginCredentials) {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    const data = await response.json();
    // Store token in localStorage
    localStorage.setItem('token', data.token);
    return data;
  }

  async register(userData: RegisterData) {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Registration failed');
    }

    const data = await response.json();
    return data;
  }

  async getCurrentUser() {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user data');
    }

    const data = await response.json();
    return data.user;
  }

  async updateProfile(profileData: FormData | UpdateProfileData) {
    const isFormData = profileData instanceof FormData;
    
    const response = await fetch(`${API_BASE_URL}/users/profile`, {
      method: 'PUT',
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...this.getAuthHeader(),
      },
      body: isFormData ? (profileData as FormData) : JSON.stringify(profileData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update profile');
    }

    const data = await response.json();
    return data.user;
  }

  async changePassword(passwordData: ChangePasswordData) {
    const response = await fetch(`${API_BASE_URL}/users/change-password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(passwordData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to change password');
    }

    return await response.json();
  }

  async forgotPassword(email: string) {
    const response = await fetch(`${API_BASE_URL}/auth/forgotpassword`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send password reset email');
    }
    return await response.json();
  }

  async resendVerification(email: string) {
    const response = await fetch(`${API_BASE_URL}/auth/resendverification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to resend verification email');
    return data;
  }

  async resetPassword(token: string, password: string) {
    const response = await fetch(`${API_BASE_URL}/auth/resetpassword/${token}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to reset password');
    }
    return await response.json();
  }

  async getAllProducts(filters?: { search?: string; category?: string; location?: string }) {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.location) params.append('location', filters.location);

    const response = await fetch(`${API_BASE_URL}/products?${params.toString()}`);

    if (!response.ok) {
      throw new Error('Failed to fetch products');
    }

    const data = await response.json();
    return data.products;
  }

  async getProduct(id: string) {
    const response = await fetch(`${API_BASE_URL}/products/${id}`);

    if (!response.ok) {
      throw new Error('Failed to fetch product');
    }

    const data = await response.json();
    return data.product;
  }

  async createProduct(formData: FormData) {
    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create product');
    }

    const data = await response.json();
    return data.product;
  }

  async updateProduct(id: string, productData: FormData | Record<string, any>) {
    const isFormData = productData instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'PUT',
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...this.getAuthHeader(),
      },
      body: isFormData ? productData : JSON.stringify(productData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update product');
    }

    const data = await response.json();
    return data.product;
  }

  async deleteProduct(id: string) {
    try {
      console.log('Attempting to delete product:', id);
      const headers = this.getAuthHeader();
      console.log('Delete request headers:', headers);
      
      const response = await fetch(`${API_BASE_URL}/products/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
      });

      console.log('Delete response status:', response.status);
      const data = await response.json();
      console.log('Delete response data:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete product');
      }

      return data;
    } catch (error) {
      console.error('Error in deleteProduct:', error);
      throw error;
    }
  }

  async getMyProducts() {
    try {
      const headers = this.getAuthHeader();
      console.log('Fetching my products with headers:', headers);
      console.log('API URL:', `${API_BASE_URL}/products/my-products`);
      
      const response = await fetch(`${API_BASE_URL}/products/my-products`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
      });

      console.log('Response status:', response.status);
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        throw new Error(errorData.message || 'Failed to fetch your products');
      }

      const data = await response.json();
      console.log('API Response data:', data);
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch products');
      }
      
      const products = data.products || [];
      console.log('Returning products:', products);
      return products;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  async deactivateProduct(id: string) {
    const response = await fetch(`${API_BASE_URL}/products/${id}/deactivate`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to deactivate product');
    }

    return await response.json();
  }

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
}

export const api = new ApiService();
